import {
  correctGuesses,
  GAMES,
  lettersFound,
  livesDealt,
  toReadingBand,
  wrongGuesses,
  type HangmanView,
  type ReadingBand,
} from '@hazel/shared'

import { useBarTitle } from '../layouts/bar-title.js'
import { letterFrom, useGameKeys } from './useGameKeys.js'
import { Card } from '../components/Card.js'
import { Announce } from './Announce.js'
import { useRoundFocus } from './useRoundFocus.js'
import { useLeaveOnEscape } from './useLeaveOnEscape.js'
import { LeaveGame } from './LeaveGame.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { Milestone } from './Milestone.js'
import { PlayAgain } from './PlayAgain.js'
import { PlayColumns } from './PlayColumns.js'
import { RaceTrack } from './RaceTrack.js'
import { LANE, RACE_ART, racerPicture } from './raceArt.js'
import { useHangman } from './useHangman.js'
import { useRound, type DealtRound } from './useRound.js'
import { TRACK_FOR_GAME } from '../sound/cues.js'
import { useCueOnEach, useCueWhen, useRoundEndCues, useRoundMusic } from './gameSound.js'
import { Keyboard } from './Keyboard.js'

function Word({ view }: { view: HangmanView }) {
  return (
    <p
      data-word-so-far
      className="font-display flex flex-wrap justify-center gap-1.5 text-(length:--text-hero) tracking-wider uppercase"
      // Read as a word rather than as a string of letters and blanks.
      aria-label={
        view.status === 'playing'
          ? `${view.letters.filter(Boolean).length} of ${view.letters.length} letters found`
          : `The word was ${view.answer ?? ''}`
      }
    >
      {view.letters.map((letter, index) => (
        <span
          key={index}
          className={[
            'border-ink-muted inline-flex min-w-8 justify-center border-b-4',
            letter ? 'border-transparent' : '',
          ].join(' ')}
        >
          {letter ?? ' '}
        </span>
      ))}
    </p>
  )
}

/*
 * There used to be a whole-word guess here: a box, a Guess button, and Enter
 * from the board to open it.
 *
 * It earned nothing. Anybody who knows the word can type its letters, and the
 * keyboard is the whole game — so it was a second way to do the same thing,
 * taking room on the screen and a stop in the tab order.
 *
 * Its one justification had been that the engine accepts a whole-word guess
 * and there was no way to make one without a mouse. That turned out to be a
 * reason to remove the box rather than to keep it: typing the letters *is* the
 * keyboard-only route, and it always was.
 *
 * The engine's `guess-word` action stays, and a wrong word still costs one
 * life the same as a wrong letter. Worth being exact about why: nothing calls
 * it today — the room's phone client only ever guesses letters — so it is kept
 * as a rule the engine knows rather than for a consumer that exists. This
 * removed a control, not a rule.
 */

export function Hangman({ band = '6-8' }: { band?: string }) {
  // The bar carries the name while she is here, in place of "Hazel's
  // Games". One call rather than one per screen: the chooser and the
  // board are both this component, so the name does not blink out between
  // picking a size and being dealt one.
  useBarTitle(GAMES.hangman.title)

  /*
    The screen's music, not the round's.

    On the round it started when play did, so every game had a beat of nothing
    while its round was being dealt. Asking here means it also survives another
    go, which is the same screen and should not restart the track.
  */
  useRoundMusic(TRACK_FOR_GAME.hangman)

  const { round, again, failed } = useRound<{ band: ReadingBand }>({
    game: 'hangman',
    band: toReadingBand(band),
  })

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        We couldn’t start a game just now. Have another go in a moment.
      </p>
    )
  }

  // Nothing is dealt until the server has said what to deal. A word invented
  // here would be one it could not check afterwards.
  if (!round) {
    return (
      <p role="status" className="py-16 text-center">
        Getting a word ready…
      </p>
    )
  }

  // Keyed on the seed so asking for another word deals a fresh board rather
  // than leaving the last one on screen under a new heading.
  return <HangmanRound key={round.seed} round={round} onAgain={again} />
}

function HangmanRound({
  round,
  onAgain,
}: {
  round: DealtRound<{ band: ReadingBand }>
  onAgain: () => void
}) {
  const leaveOnEscape = useLeaveOnEscape()
  const game = useHangman(round, onAgain)
  const { view, guess } = game
  const board = useRoundFocus<HTMLDivElement>(round.seed, view.status !== 'playing')

  // A laptop should be playable from the keyboard rather than by clicking
  // thirty small buttons.
  useGameKeys((event) => {
    // Before the status gate, deliberately. Leaving has nothing to do with
    // whether the round is still running — and a finished round is exactly
    // when somebody wants out, which is how this came to be reported as
    // "Escape does nothing".
    if (event.key === 'Escape') return void leaveOnEscape(false)

    if (view.status !== 'playing') {
      // Another word, without reaching for the button.
      if (event.key === 'Enter') game.again()
      return
    }

    const letter = letterFrom(event)
    if (letter) guess(letter)
  })

  /*
   * The race, from the view. Both tallies and the total are defined once, in
   * `hangman.ts` beside the view itself, because the room's host screen draws
   * this same race off this same view — and a rule kept in two places is the
   * one that comes apart when she plays with a cousin. The reasoning that used
   * to be written out here is there now.
   */
  const found = lettersFound(view)
  const correct = correctGuesses(view)
  const wrong = wrongGuesses(view)

  /*
    Sound, below the two tallies it needs, because a right letter is worked out
    here rather than reported: the view says which letters were tried and which
    are showing, and Hoppity's lane already counts the rest.

    Counted rather than watched — two wrong letters running leave the same view
    behind them, and only the tally moving says a second one happened.
  */
  useCueOnEach(correct.length, 'letter.right')
  useCueOnEach(wrong, 'letter.wrong')
  useCueWhen(view.status === 'won', 'word.won')
  useCueWhen(view.status === 'lost', 'word.lost')
  useRoundEndCues(false, game.milestone)

  return (
    <div className="mx-auto flex w-full max-w-2xl [[data-scale=tv]_&]:max-w-none flex-col gap-4">
      <Card ref={board} tabIndex={-1} className="outline-none flex flex-col items-center gap-4">
        {/*
          Hoppity and Daddy, racing to a carrot.

          Every number here is off the view: Hoppity's steps are the letter
          positions revealed, so guessing the E in THREE is three hops at once,
          and Daddy's are the wrong guesses, which is the lives that already
          existed. Nothing new is counted and nothing is kept — see DOL-38.
        */}
        <RaceTrack
          goal={RACE_ART.carrot}
          racers={[
            {
              key: 'hoppity',
              name: 'Hoppity',
              picture: racerPicture('hoppity'),
              steps: found,
              of: view.letters.length,
              tint: LANE.hoppity,
            },
            {
              key: 'daddy',
              name: 'Daddy',
              picture: RACE_ART.daddy,
              steps: wrong,
              of: livesDealt(view),
              tint: LANE.daddy,
            },
          ]}
        />

        {/* Hangman had no live region at all while playing, so a guess said
            nothing — a screen reader gave no way to tell whether E was in the
            word. Lives last, because it is the least urgent of the three and
            hearing it first delays the answer to what she just did. */}
        <Announce>
          {[
            view.status === 'won'
              ? 'You got it!'
              : view.status === 'lost'
                ? `The word was ${view.answer ?? ''}.`
                : view.lastGuess
                  ? `${view.lastGuess.letter.toUpperCase()} is ${view.lastGuess.correct ? 'in the word' : 'not in it'}.`
                  : '',
            view.status === 'playing'
              ? `${view.letters.filter(Boolean).length} of ${view.letters.length} letters found. ${view.livesRemaining} lives left.`
              : '',
            game.milestone ? `That’s ${game.milestone} games played.` : '',
          ]
            .filter(Boolean)
            .join(' ')}
        </Announce>

        {/*
          The word sits after the live region in both states, deliberately.

          It used to come before it, and splitting the screen into columns put
          it after — but only while playing, because the finished screen has no
          columns. So the order somebody reading linearly met depended on
          whether the round was still going, which is the sort of thing nobody
          decides and everybody inherits.

          After, rather than back to before, because that is the order the two
          make sense in: what just happened, then the word as it now stands.
          Pinned by a test so it stays a decision.
        */}
        {view.status === 'playing' ? (
          <PlayColumns
            reading={
              <>
                <Word view={view} />

                {/* Under the blanks, because the two are read together — a
                    clue beside the keyboard is beside the wrong half. It was
                    put there when the keys moved into their own column, which
                    was the moment to notice. */}
                <p className="text-(length:--text-heading) [[data-scale=tv]_&]:text-(length:--text-title)">
                  Clue: {view.hint}
                </p>
              </>
            }
            answering={
              <>
                <Keyboard
                  guessed={view.guessed}
                  correct={correct}
                  disabled={false}
                  onGuess={guess}
                />
              </>
            }
          />
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Word view={view} />

            {view.status === 'won' ? (
              <>
                <p className="text-(length:--text-title) font-display">You got it!</p>
                <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">
                  Nice guessing.
                </p>
              </>
            ) : (
              <>
                {/* No sad noises, no red crosses. Meeting a word she did not
                    know is not a failure, and the app should not say it is. */}
                <p className="text-(length:--text-title) font-display">
                  The word was {view.answer?.toUpperCase()}
                </p>
                <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">
                  A tricky one. Now you know it.
                </p>
              </>
            )}

            {game.milestone ? <Milestone games={game.milestone} /> : null}

            <PlayAgain label="Another go" does="to play again" onAgain={game.again} />
          </div>
        )}
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={
          view.status === 'playing'
            ? [{ keys: ['A', '–', 'Z'], does: 'Guess a letter' }, LEAVE_SHORTCUT]
            : [{ keys: ['Enter'], does: 'Another word' }, LEAVE_SHORTCUT]
        }
      />
    </div>
  )
}
